import React from 'react';
import PropTypes from 'prop-types';
import { uiState } from 'react-redux-ui-state';
import { withStyles } from '@material-ui/core';
import Hidden from '@material-ui/core/Hidden';
import Lock from '@material-ui/icons/Lock';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Card from '@material-ui/core/Card';

import ORAlert from 'components/ORAlert/ORAlert';
import ORTypography from 'components/ORTypography/ORTypography';
import RBACDeleteButton from 'components/RBACWrapper/RBACDeleteButton/RBACDeleteButton';
import SegmentClickTracker from 'components/SegmentClickTracker/SegmentClickTrackerContainer';
import roleDisabled from 'services/roleDisabled/index';
import { OWNER, ADMIN, OR_ADMIN, OR_SUPERUSER } from 'constants/roles';
import ACL_KINDS from '../../constants/aclKinds';
import AclFormContainer from '../AclForm/AclFormContainer';
import FormToggleButton from '../FormToggleButton/FormToggleButton';
import ORTable from '../ORTable/ORTable';
import PopoutItem from '../PopoutItem/PopoutItem';
import { handleClickAway } from '../../services/handleClickAway/index';

const propTypes = {
  classes: PropTypes.shape({}).isRequired,
  acls: PropTypes.instanceOf(Array).isRequired,
  open: PropTypes.bool.isRequired,
  setUiState: PropTypes.func.isRequired,
  deleteInstanceAcl: PropTypes.func.isRequired,
  instanceService: PropTypes.string.isRequired,
  instanceId: PropTypes.string.isRequired,
  userRoles: PropTypes.arrayOf(PropTypes.string).isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

export function UnstyledAclList({
  acls,
  open,
  setUiState,
  classes,
  deleteInstanceAcl,
  instanceService,
  instanceId,
  userRoles,
  page,
  rowsPerPage,
}) {
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = React.useState(false);
  const [aclToDelete, setAclToDelete] = React.useState(null);

  function handleDeleteButtonClick(acl) {
    setDeleteConfirmationOpen(true);
    setAclToDelete(() => acl);
  }

  function handleConfirmationOk() {
    deleteInstanceAcl(instanceService, instanceId, aclToDelete.id);
    setDeleteConfirmationOpen(false);
  }

  function handleConfirmationCancel() {
    setDeleteConfirmationOpen(false);
    setAclToDelete(null);
  }

  function handleChangePage(event, newPage) {
    setUiState({
      page: newPage,
    });
  }

  const allowedUserRoles = [OWNER, ADMIN, OR_ADMIN, OR_SUPERUSER];
  let anchorEl;
  function handleClick() {
    setUiState({
      open: !open,
    });
  }

  const setRef = (node) => {
    anchorEl = node;
  };

  const getAnchorEl = () => anchorEl;

  const aclForm = (
    <AclFormContainer
      instanceService={instanceService}
      instanceId={instanceId}
      newInstanceAcls={acls}
      closePopout={handleClick}
    />
  );

  const aclFormButton =
    acls.length === 0 ? null : (
      <SegmentClickTracker buttonName="Firewall (Instance Details)">
        <FormToggleButton
          openForm={handleClick}
          setRef={setRef}
          label="Firewall"
          icon={<Lock />}
          disabled={roleDisabled(userRoles, allowedUserRoles)}
        />
      </SegmentClickTracker>
    );

  const embeddedAclForm =
    acls.length === 0 ? (
      <Card className={classes.embeddedAclFormContainer}>
        <AclFormContainer
          instanceService={instanceService}
          instanceId={instanceId}
          newInstanceAcls={acls}
          formType="embedded"
        />
      </Card>
    ) : null;

  const aclRows = acls.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((acl) => (
    <TableRow key={acl.id}>
      <Hidden smDown>
        <TableCell component="td" scope="row">
          {acl.name}
          {acl.cidr === '0.0.0.0/0' && (
            <ORTypography component="p" className={classes.openAclWarning}>
              0.0.0.0/0 ACLs can be dangerous, as they allow anyone to attempt to log into your
              instance. Please ensure you&apos;re using strong passwords and/or narrow your ACLs to
              trusted ranges.
            </ORTypography>
          )}
        </TableCell>
        <TableCell
          style={{ verticalAlign: acl.cidr === '0.0.0.0/0' ? 'top' : 'middle' }}
          align="left"
        >
          {acl.cidr}
        </TableCell>
        <TableCell
          style={{ verticalAlign: acl.cidr === '0.0.0.0/0' ? 'top' : 'middle' }}
          align="left"
        >
          {acl.kind === 1 || typeof ACL_KINDS[acl.kind] === 'undefined'
            ? ''
            : ACL_KINDS[acl.kind].label}
        </TableCell>
      </Hidden>
      <Hidden mdUp>
        <TableCell component="td" scope="row">
          {acl.name}
          <br />
          {acl.cidr}
          <br />
          {acl.kind === 1 || typeof ACL_KINDS[acl.kind] === 'undefined'
            ? ''
            : ACL_KINDS[acl.kind].label}
          {acl.cidr === '0.0.0.0/0' && (
            <ORTypography component="p" className={classes.openAclWarning}>
              0.0.0.0/0 ACLs can be dangerous, as they allow anyone to attempt to log into your
              instance. Please ensure you&apos;re using strong passwords and/or narrow your ACLs to
              trusted ranges.
            </ORTypography>
          )}
        </TableCell>
      </Hidden>
      <TableCell style={{ paddingBottom: acl.cidr === '0.0.0.0/0' ? '25px' : '8px' }} align="right">
        <RBACDeleteButton
          size="small"
          userAccess={userRoles}
          allowedAccess={allowedUserRoles}
          onClick={() => handleDeleteButtonClick(acl)}
        />
      </TableCell>
    </TableRow>
  ));

  const aclTable =
    acls.length === 0 ? null : (
      <div className={classes.tableWrapper}>
        <ORTable
          count={acls.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onChangePage={handleChangePage}
        >
          <Hidden smDown>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>CIDR</TableCell>
                <TableCell>Role</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
          </Hidden>
          <Hidden mdUp>
            <TableHead>
              <TableRow>
                <TableCell>ACL</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
          </Hidden>
          <TableBody>{aclRows}</TableBody>
        </ORTable>
      </div>
    );

  return (
    <React.Fragment>
      <ORAlert
        open={deleteConfirmationOpen}
        message={`Are you sure you want to delete the Firewall IP ${
          aclToDelete ? aclToDelete.name : ''
        }? This action cannot be undone.`}
        onOk={handleConfirmationOk}
        onCancel={handleConfirmationCancel}
      />
      <div className={classes.listContainer}>
        {embeddedAclForm}
        <div className={classes.ipButtonContainer}>
          {aclFormButton}
          <PopoutItem
            anchorEl={getAnchorEl}
            handleClick={handleClick}
            handleClickAway={(e) => handleClickAway(e, anchorEl, () => setUiState({ open: false }))}
            placement="bottom"
            open={open}
          >
            {aclForm}
          </PopoutItem>
        </div>
        {aclTable}
      </div>
    </React.Fragment>
  );
}

UnstyledAclList.propTypes = propTypes;

const styles = (theme) => ({
  embeddedAclFormContainer: {
    maxWidth: '900px',
    marginRight: 'auto',
    marginLeft: 'auto',
    boxShadow: '3px 5px 5px 3px rgba(216,216,216,1)',
  },
  listItemDeleteRoot: {
    color: theme.customColors.white,
    backgroundColor: theme.customColors.red,
  },
  listContainer: {
    position: 'relative',
  },
  ipButtonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    verticalAlign: 'center',
    [theme.breakpoints.down('sm')]: {
      alignItems: 'center',
    },
  },
  tableWrapper: {
    '& th': {
      height: '20px',
      paddingTop: '15px',
      paddingBottom: '5px',
    },
    '& td': {
      padding: theme.spacing(1),
    },
    '& td:first-child': {
      paddingLeft: theme.spacing(2),
    },
  },
  openAclWarning: {
    color: theme.customColors.red,
    fontStyle: 'italic',
    fontSize: '.8rem',
    marginTop: theme.spacing(1),
    maxWidth: '500px',
  },
});

export const uiConfig = {
  name: 'aclList',
  state: () => ({
    open: false,
    page: 0,
    rowsPerPage: 10,
  }),
};
export default uiState(uiConfig)(withStyles(styles)(UnstyledAclList));
